import React, { useEffect, useState } from 'react';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Modal from '@components/common/Modals/Modal/Modal';
import Text from '@components/common/Text/Text';
import Confidentiality from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/Confidentiality/Confidentiality';
import GeneralForm from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/General/GeneralForm';
import ParticipationsForm from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/Participations/ParticipationsForm';
import Revives from '@components/lpc/Pot/AmountModule/Settings/SettingsModal/Revives/Revives';
import { inviteType } from '@propTypes/inviteType';
import { potType } from '@propTypes/potTypes';
import potService from '@services/domain/Lpc/PotService';
import ObjectService from '@services/utils/ObjectService';

import css from './SettingsModal.module.scss';

type SettingsModalProps = {
    isVisible: boolean;
    onHide: () => void;
    onConfirm: () => void;
    onShowReviveSuccessModal: () => void;
    newPoolObjective: (potObjectiveType) => void;
    pot: potType;
};

const SettingsModal = ({ isVisible, onHide, pot, onShowReviveSuccessModal, newPoolObjective }: SettingsModalProps) => {
    const { t } = useTranslateLpc(['lpc-pot']);

    const [activeTab, setActiveTab] = useState(0);
    const [revives, setRevives] = useState<inviteType[]>([]);
    const [tabsCount, setTabsCount] = useState<number>(3);

    const onCancel = () => {
        onHide();
    };

    useEffect(() => {
        getInvites();
    }, []);

    const getInvites = () => {
        potService.getInvites(pot.uniq).then((res: inviteType[]) => {
            setRevives(processRevives(res));
        });
    };

    const processRevives = (res) => {
        if (ObjectService.hasContent(res)) {
            return res.map((r, index) => {
                r.index = index;
                return r;
            });
        }
        return res;
    };

    useEffect(() => {
        ObjectService.hasContent(revives) ? setTabsCount(4) : setTabsCount(3);
    }, [revives]);

    return (
        <Modal visible={isVisible} onHide={onHide} customClass={css.custom}>
            <h5>{t('settings.settings')}</h5>
            <div className={css.form__divider} />

            <div className={css.container}>
                {/* NAV */}
                <section className={css.tabs}>
                    {[...new Array(tabsCount)].map((_, i) => (
                        <span
                            key={i}
                            onClick={() => setActiveTab(i)}
                            className={`${css.tabs__cta} ${activeTab === i && css.tabs__cta__active}`}
                        >
                            <img src={`/front-static/icons/pot/tab-settings-${i}.svg`} alt="settings" />
                            <Text variant={'caption_01'} color={'txt-primary'}>
                                {t(`settings.tabs.${i}`)}
                            </Text>
                        </span>
                    ))}
                </section>
                {/* FORM */}
                {activeTab === 0 && (
                    <section className={css.form}>
                        <GeneralForm
                            onCloseModal={() => onCancel()}
                            potRef={pot.uniq}
                            pot={pot}
                            potLink={pot.absoluteUrl}
                            customUrl={pot.customUniq}
                            potStatus={pot.status}
                            askForMessage={pot.askForMessage}
                            amountLeft={pot.amountLeft}
                            newPoolObjective={(objective) => newPoolObjective(objective)}
                        />
                    </section>
                )}
                {activeTab === 1 && (
                    <section className={css.form}>
                        <ParticipationsForm
                            onCloseModal={() => onCancel()}
                            potRef={pot.uniq}
                            poolContribution={pot.poolContribution}
                        />
                    </section>
                )}
                {activeTab === 2 && (
                    <section className={css.form}>
                        <Confidentiality onCloseModal={() => onCancel()} potRef={pot.uniq} privacy={pot.privacy} />
                    </section>
                )}
                {activeTab === 3 && (
                    <section className={css.form}>
                        <Revives
                            onShowReviveSuccessModal={() => onShowReviveSuccessModal()}
                            onUpdateRevives={() => getInvites()}
                            onCloseModal={() => onCancel()}
                            potRef={pot.uniq}
                            revives={revives}
                        />
                    </section>
                )}
            </div>
        </Modal>
    );
};
export default SettingsModal;
